import {
  resumeBlobFromResponse,
  resumeDownloadName,
} from 'src/helpers/resumeFile';
import apiClient from 'src/helpers/apiClient';
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from 'src/redux/index';
import { enqueueSnackbar } from 'notistack';
import { getFormattedErr } from 'src/helpers';
import axios from 'axios';
import {
  setHiringStatus,
  startBulkAnalysis,
  markApplicantReanalyzing,
} from 'src/redux/slices/applicantReview';
import type {
  ApplicantListParam,
  BulkAddJobApplicantMessageBody,
  BulkApplicantActionBody,
  BulkResumeApplicantUserIdsBody,
  DownloadApplicantResumeBody,
  GetFilteredApplicantUserIdsBody,
  HiringApiApiHiringApiGetApplicantPlacementRequest,
  InsertAssessmentScoreBody,
  SendAssessmentMessageBody,
  UpdateHiringStatusBody,
  UpdateJobApplicantTagBody,
} from 'src/openapi';
import config from '../../../config';

export const fetchAbekusProfile = createAsyncThunk(
  'user/public_profile',
  async (
    {
      username,
      onlyAnalytics,
      onlyAttempted = true,
      page = 1,
      pageSize = 10,
    }: {
      username: string;
      onlyAnalytics?: boolean;
      onlyAttempted?: boolean;
      page?: number;
      pageSize?: number;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetUserPublicProfile(
        {
          username,
          onlyAnalytics,
          onlyAttempted,
          pageNo: page,
          pageSize,
          totalPages: undefined,
        },
        {
          signal,
        },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

/* eslint no-unused-vars: "off" */
export const fetchJobApplicants = createAsyncThunk(
  'jobs/fetchJobApplicants',
  async (
    {
      jobId,
      orgId,
      payload,
      selectFirstApplicant,
      selectedApplicantUsername,
      skipStore,
    }: {
      jobId: string;
      orgId: string;
      payload: ApplicantListParam;
      selectFirstApplicant?: boolean;
      selectedApplicantUsername?: string;
      skipStore?: boolean;
    },
    { rejectWithValue, fulfillWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetJobApplicantsList({
        jobId,
        orgId,
        applicantListParam: payload,
      });
      return fulfillWithValue({
        data: response.data,
        selectFirstApplicant,
        selectedApplicantUsername,
        skipStore,
      });
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchNextJobApplicants = createAsyncThunk(
  'jobs/fetchNextJobApplicants',
  async (
    {
      jobId,
      orgId,
      payload,
    }: {
      jobId: string;
      orgId: string;
      payload: ApplicantListParam;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetJobApplicantsList({
        jobId,
        orgId,
        applicantListParam: payload,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const candidateBulkAction = createAsyncThunk(
  'job/candidateBulkAction',
  async (
    { jobId, obj, orgId }: { jobId: string; obj: any; orgId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiBulkActionOnApplicants({
        jobId,
        orgId,
        bulkApplicantActionBody: obj,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateHiringStatus = createAsyncThunk(
  'jobs/updateHiringStatus',
  async (
    {
      jobId,
      userId,
      obj,
      orgId,
    }: {
      jobId: string;
      userId: string;
      obj: UpdateHiringStatusBody;
      orgId: string;
    },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiUpdateHiringStatus({
        applicantId: userId,
        jobId,
        orgId,
        updateHiringStatusBody: obj,
      });
      dispatch(setHiringStatus(response.data));
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const postNotes = createAsyncThunk(
  'jobs/postNotes',
  async (
    {
      jobId,
      userId,
      obj,
      orgId,
    }: {
      jobId: string;
      userId: string;
      obj: any;
      orgId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiCreateApplicantNote({
        applicantId: userId,
        jobId,
        orgId,
        createApplicantNoteBody: obj,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getApplicantDetails = createAsyncThunk(
  'jobs/getapplicantdetails',
  async (
    {
      jobId,
      applicantId,
      orgId,
    }: {
      jobId: string;
      applicantId: string;
      orgId: string;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetJobApplicantsDetails(
        { applicantId, jobId, orgId },
        { signal },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getJobByIdForEmployer = createAsyncThunk(
  'job/{job_id}/employer-job-details',
  async ({ jobId }: { jobId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetJobByIdForEmployer({ jobId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const DownloadCsv = createAsyncThunk(
  `jobs/download-csv`,
  async (
    {
      jobId,
      orgId,
      payload,
    }: {
      jobId: string;
      orgId: string;
      payload: GetFilteredApplicantUserIdsBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiCreateJobCandidateCsv(
        { jobId, orgId, getFilteredApplicantUserIdsBody: payload },
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(
        new Blob([(response as any).data]),
      );

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'candidates.csv');
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const DownloadResume = createAsyncThunk(
  `jobs/download-Resume`,
  async (
    {
      jobId,
      orgId,
      applicantId,
      userName,
      downloadApplicantResumeBody,
    }: {
      applicantId: string;
      jobId: string;
      orgId: string;
      downloadApplicantResumeBody: DownloadApplicantResumeBody;
      userName?: string;
    },
    { getState, rejectWithValue, fulfillWithValue },
  ) => {
    const url = `${config.API_URL}/org/${orgId}/job/${jobId}/applicant/${applicantId}/resume-pdf`;
    const state = getState() as RootState;
    try {
      const response = await axios.post(url, downloadApplicantResumeBody, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${state.auth?.token as string}`,
        },
      });

      const blob = resumeBlobFromResponse(response);
      const url1 = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url1;
      link.setAttribute(
        'download',
        resumeDownloadName(
          userName ? `${userName} resume` : 'resume',
          blob.type,
        ),
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url1);

      return fulfillWithValue({
        status: 'success',
        message: 'resume downloaded',
      });
    } catch (err: any) {
      return rejectWithValue(
        JSON.parse((await err?.response?.data?.text()) ?? ''),
      );
    }
  },
);

export const GiveRating = createAsyncThunk(
  `jobs/give-rating`,
  async ({ jobId, orgId, applicantId, ratingValue }: any) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiUpdateApplicantRating({
        applicantId,
        jobId,
        orgId,
        ratingValue: String(ratingValue),
      });
      return response.data;
    } catch (err: any) {
      throw err.response.data;
    }
  },
);

export const deleteNote = createAsyncThunk(
  `jobs/delete-Note`,
  async (
    { jobId, orgId, noteId }: { jobId: string; orgId: string; noteId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiDeleteApplicantNote({ jobId, noteId, orgId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const editNote = createAsyncThunk(
  `jobs/edit-Note`,
  async (
    {
      jobId,
      orgId,
      noteId,
      noteTxt,
    }: { jobId: string; orgId: string; noteId: string; noteTxt: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiUpdateApplicantNote({
        jobId,
        noteId,
        orgId,
        createApplicantNoteBody: {
          message: noteTxt,
        },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const addUpdateApplicantTag = createAsyncThunk(
  `jobs/add-edit-applicant-tag`,
  async (
    {
      jobId,
      orgId,
      body,
    }: { jobId: string; orgId: string; body: Array<UpdateJobApplicantTagBody> },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiUpdateJobApplicantTag({
        jobId,
        orgId,
        updateJobApplicantTagBody: body,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getApplicantTag = createAsyncThunk(
  `jobs/get-applicant-tag`,
  async (
    {
      jobId,
      orgId,
      searchTerm,
    }: { jobId: string; orgId: string; searchTerm?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetApplicantTags({ jobId, orgId, searchQuery: searchTerm });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchApplicantResume = createAsyncThunk(
  'fetch-resume',
  async (
    {
      applicantId,
      jobId,
      orgId,
    }: {
      applicantId: string;
      jobId: string;
      orgId: string;
    },
    { signal, rejectWithValue, getState, fulfillWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const res = await axios.post(
        `${config.API_URL}/org/${orgId}/job/${jobId}/applicant/${applicantId}/resume-pdf`,
        {
          is_download: false,
          user_uploaded_resume: true,
        },
        {
          signal,
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${state.auth.token}`,
          },
        },
      );

      const blob = resumeBlobFromResponse(res);
      const url = window.URL.createObjectURL(blob);
      return fulfillWithValue({ data_url: url, content_type: blob.type });
    } catch (err: any) {
      return rejectWithValue(
        JSON.parse((await err?.response?.data?.text()) ?? ''),
      );
    }
  },
);

export const fetchJobIdOrgId = createAsyncThunk(
  'jobs/jobId-orgId',
  async ({ jobAlias }: { jobAlias: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetOrgJobIds({ jobAlias });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const assessmentScore = createAsyncThunk(
  'jobs/assessment-score',
  async (
    {
      applicantId,
      jobId,
      orgId,
      score,
    }: {
      applicantId: string;
      jobId: string;
      orgId: string;
      score: InsertAssessmentScoreBody[];
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiSetAssessmentResponseScore({
        applicantId,
        jobId,
        orgId,
        insertAssessmentScoreBody: score,
      });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

let getSkillListController: AbortController | null = null;
export const getSkillsList = createAsyncThunk(
  'job/get-user-skill',
  async (query: string, { rejectWithValue }) => {
    if (getSkillListController) {
      getSkillListController.abort();
    }

    getSkillListController = new AbortController();
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetUserSkill(
        { pageNo: undefined, pageSize: undefined, query },
        {
          signal: getSkillListController.signal,
        },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getLabel = createAsyncThunk('level', async (_, { signal }) => {
  const response = await (
    await apiClient.getLevelApi()
  ).levelApiGetAllLevels({ signal });
  return response.data;
});

export const bulkStatusUpdate = createAsyncThunk(
  'resume/bulk-status-update',
  async (
    {
      jobId,
      orgId,
      payload,
    }: {
      jobId: string;
      orgId: string;
      payload: BulkApplicantActionBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiBulkActionOnApplicants({
        jobId,
        orgId,
        bulkApplicantActionBody: payload,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const sendBulkMessage = createAsyncThunk(
  'resume/send-bulk-message',
  async (
    {
      jobId,
      orgId,
      payload,
    }: {
      jobId: string;
      orgId: string;
      payload: BulkAddJobApplicantMessageBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiBulkAddJobApplicantMessage({
        jobId,
        orgId,
        bulkAddJobApplicantMessageBody: payload,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const bulkResumeDownload = createAsyncThunk(
  'resume/downalod-bulk-resume',
  async (
    {
      jobId,
      orgId,
      payload,
    }: {
      jobId: string;
      orgId: string;
      payload: BulkResumeApplicantUserIdsBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getFilesApi()
      ).filesApiBulkDownloadResume(
        { jobId, orgId, bulkResumeApplicantUserIdsBody: payload },
        { responseType: 'blob' },
      );

      const url = window.URL.createObjectURL(
        new Blob([(response as any).data]),
      );

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'candidates_resume.zip');
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAnalysedResume = createAsyncThunk(
  'job/getAnalysedResume',
  async (
    {
      applicantId,
      jobId,
      orgId,
    }: {
      applicantId: string;
      jobId: string;
      orgId: string;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetApplicantResumeAnalysisByUserId(
        { applicantId, jobId, orgId },
        { signal },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getJobPlanDetails = createAsyncThunk(
  'premium/plan/{plan_id}',
  async ({ planId }: { planId: string }, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getOrgApi()
      ).organizationApiGetPlanById({ planId });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getJobApplicantsMetaData = createAsyncThunk(
  'get/get-job-applicants-meta-data',
  async (
    { jobId, orgId }: { jobId: string; orgId: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getJobApi()
      ).jobApiGetJobApplicantsMetaData({ jobId, orgId });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getJobCompetitionAnsweredQuestion = createAsyncThunk(
  'get/get-job-applicants-meta-data',
  async (
    {
      jobId,
      applicantUserId,
      competitionId,
      page = 1,
      pageSize = 10,
    }: {
      applicantUserId: string;
      competitionId: string;
      jobId: string;
      page?: number;
      pageSize?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getJobApi()
      ).jobApiGetJobCompetitionAnsweredQuestions({
        applicantUserId,
        competitionId,
        jobId,
        page,
        pageSize,
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const reanalyzeApplicant = createAsyncThunk(
  'jobs/reanalyzeApplicant',
  async (
    {
      orgId,
      jobId,
      applicantId,
    }: { orgId: string; jobId: string; applicantId: string },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiReanalyzeApplicant({
        applicantId: orgId,
        jobId,
        orgId: applicantId,
      });
      // Optimistically flip the ring back to "analyzing" so the click has
      // immediate feedback; polling then lands the real terminal status.
      dispatch(
        markApplicantReanalyzing({
          applicantId,
          at: new Date().toISOString(),
        }),
      );
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const requestBulkAnalysis = createAsyncThunk(
  'jobs/requestBulkAnalysis',
  async (
    {
      jobId,
      orgId,
      filters,
    }: {
      jobId: string;
      orgId: string;
      // Same filter payload the applicant list builds (formatApplicantListFilters).
      // The bulk endpoint accepts the same shape, so we cast at the boundary
      // rather than re-deriving the filter object.
      filters: ApplicantListParam;
    },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiRequestBulkAnalysis({
        jobId,
        orgId,
        getFilteredApplicantUserIdsBody:
          filters as unknown as GetFilteredApplicantUserIdsBody,
      });
      const count = response.data.count ?? 0;
      dispatch(
        startBulkAnalysis({
          batchId: response.data.batch_id ?? null,
          total: count,
        }),
      );
      return response.data;
    } catch (err: any) {
      const errorKey = err?.response?.data?.error;
      // Insufficient credits is surfaced by opening the buy-credits dialog in
      // the employerCredit slice; suppress the generic snackbar for it.
      if (errorKey !== 'insufficient_employer_credits') {
        enqueueSnackbar(getFormattedErr(errorKey), { variant: 'error' });
      }
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const fetchApplicantAiAccess = createAsyncThunk(
  'jobs/fetchApplicantAiAccess',
  async (
    { jobId, orgId }: { jobId: string; orgId: string },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetApplicantAiAccessStatus({ jobId, orgId }, { signal });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const unlockApplicantAiScores = createAsyncThunk(
  'jobs/unlockApplicantAiScores',
  async (
    { jobId, orgId }: { jobId: string; orgId: string },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiUnlockApplicantAiScores({ jobId, orgId });
      // Seed the existing bulk-progress UI; results then stream over the
      // applicant_resume_analysis_complete + bulk_analysis_progress sockets.
      dispatch(
        startBulkAnalysis({
          batchId: response.data.batch_id ?? null,
          total: response.data.count ?? 0,
        }),
      );
      return response.data;
    } catch (err: any) {
      const errorKey = err?.response?.data?.error;
      // Insufficient credits opens the buy-credits dialog (employerCredit slice);
      // suppress the generic snackbar for it.
      if (errorKey !== 'insufficient_employer_credits') {
        enqueueSnackbar(getFormattedErr(errorKey), { variant: 'error' });
      }
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const sendJobAssessmentMessage = createAsyncThunk(
  'put/send-assessment-message-to-applicants',
  async (
    {
      jobId,
      orgId,
      payload,
    }: {
      jobId: string;
      orgId: string;
      payload: SendAssessmentMessageBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getJobApi()
      ).jobApiSendAssessmentMessageToApplicants({
        jobId,
        orgId,
        sendAssessmentMessageBody: payload,
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const getApplicantPlacement = createAsyncThunk(
  'put/send-assessment-message-to-applicants',
  async (
    payload: HiringApiApiHiringApiGetApplicantPlacementRequest,
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getHiringApi()
      ).hiringApiGetApplicantPlacement(payload);
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
